import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import {
    MenuPermissionActionEnum, RootMenuEnum, XpressUserRoleEnum,
    XpressClientLocationTypeEnum, ServiceTypeEnum,
    MenuPermissionConfig
} from 'app/models';
import { Utils } from 'app/utils';
import { CustomWindow } from 'app/@types';

declare let window: CustomWindow;

export interface PermissionDto {
    CanRead: boolean;
    CanCreate: boolean;
    CanDelete: boolean;
    CanUpdate: boolean;
}

@Injectable()
export class AppDataService {
    public authData: {
        access_token: string;
        UserId: string;
        FirstName: string;
        LastName: string;
        userName: string;
        Email: string;
        ImageUrl: string;
        roleId: string;
        riderId: string;
        MerchantId: string;
        MerchantUserId: string;
        OperatorId: string;
        TourOperatorId: string;
        merchantRoleId: string;
        Permissions: { [key: string]: PermissionDto };
        BetaFeatures: string;
        BuildNumber: string;
        ClientLocationTypes: any;
    } = {
            access_token: '',
            UserId: '0',
            FirstName: '',
            LastName: '',
            userName: '',
            Email: '',
            ImageUrl: '',
            roleId: '0',
            riderId: '0',
            MerchantId: '0',
            MerchantUserId: '0',
            OperatorId: '0',
            TourOperatorId: '0',
            merchantRoleId: '0',
            Permissions: {},
            BetaFeatures: '',
            BuildNumber: '',
            ClientLocationTypes: []
        };

    public xpressUserRoleId: XpressUserRoleEnum = XpressUserRoleEnum.None;

    public activeRootMenu: RootMenuEnum = RootMenuEnum.Admin;

    public merchanUserSource: BehaviorSubject<any | null> = new BehaviorSubject(null);
    public merchanUserSource$ = this.merchanUserSource.asObservable();

    public isBoracayLocation: boolean = false;
    public isDumagueteLocation: boolean = false;

    private clientLocationType = new BehaviorSubject<XpressClientLocationTypeEnum>(this.getDefaultClientLocationType());
    public clientLocationType$ = this.clientLocationType.asObservable();

    private authDataLoaded = new BehaviorSubject<boolean>(false);
    public authDataLoaded$ = this.authDataLoaded.asObservable();

    constructor() {
        const environmentName = window['environmentName'];
        this.isBoracayLocation = this.isBoracayLocationFunc();
        this.isDumagueteLocation = this.isDumagueteLocationFunc();
    }

    setClientLocationType(type: XpressClientLocationTypeEnum) {
        sessionStorage.setItem('clientLocationType', JSON.stringify(type));
        window.sessionStorage.removeItem('currentMenuItem');

        this.isBoracayLocation = (type === XpressClientLocationTypeEnum.Boracay);
        this.isDumagueteLocation = (type === XpressClientLocationTypeEnum.Dumaguete);
        this.clientLocationType.next(type);
    }

    getClientLocationType(): XpressClientLocationTypeEnum {
        return this.clientLocationType.getValue();
    }

    private getDefaultClientLocationType(): XpressClientLocationTypeEnum {
        const clientLocationTypeSessionStorage = sessionStorage.getItem('clientLocationType');
        if (clientLocationTypeSessionStorage && !isNaN(+clientLocationTypeSessionStorage)) {
            return +clientLocationTypeSessionStorage as XpressClientLocationTypeEnum;
        }

        return XpressClientLocationTypeEnum.Xpress;
    }

    private isBoracayLocationFunc(): boolean {
        const clientLocationTypeSessionStorage = sessionStorage.getItem('clientLocationType');

        if (clientLocationTypeSessionStorage && !isNaN(+clientLocationTypeSessionStorage)) {
            const clientLocationType = +clientLocationTypeSessionStorage as XpressClientLocationTypeEnum;

            return clientLocationType === XpressClientLocationTypeEnum.Boracay;
        }

        return false;
    }

    private isDumagueteLocationFunc(): boolean {
        const clientLocationTypeSessionStorage = sessionStorage.getItem('clientLocationType');

        if (clientLocationTypeSessionStorage && !isNaN(+clientLocationTypeSessionStorage)) {
            const clientLocationType = +clientLocationTypeSessionStorage as XpressClientLocationTypeEnum;

            return clientLocationType === XpressClientLocationTypeEnum.Dumaguete;
        }

        return false;
    }

    public hasPermission(permission: string, action: MenuPermissionActionEnum): boolean {
        const permissionDto = this.authData.Permissions[permission];
        if (!permissionDto) {
            return false;
        }

        switch (action) {
            case MenuPermissionActionEnum.Read: return permissionDto.CanRead;
            case MenuPermissionActionEnum.Create: return permissionDto.CanCreate;
            case MenuPermissionActionEnum.Update: return permissionDto.CanUpdate;
            case MenuPermissionActionEnum.Delete: return permissionDto.CanDelete;
            default: return false;
        }
	}

	public isAuthenticated(): boolean {
		return !!this.authData.access_token;
	}

    public setAuthDataLoaded(loaded: boolean): void {
        this.authDataLoaded.next(loaded);
    }

    public isAuthDataLoaded(): boolean {
        return this.authDataLoaded.getValue();
    }

    public isMerchant(): boolean {
        return (!!this.authData.MerchantId && this.authData.MerchantId !== '0');
    }

    public isAdmin(): boolean {
        return +this.authData.roleId === XpressUserRoleEnum.Admin;
    }

    public isTourOperatorManager(): boolean {
        return this.hasPermission(MenuPermissionConfig.EXPERIENCE_OPERATOR_REG, MenuPermissionActionEnum.Read);
    }

    protected currentPathname(): string {
        return window.location.pathname || '';
    }

    protected redirectTo(url: string): void {
        window.location.href = url;
    }

    public logout(): void {
        // Clear stored ConversationId (used in logging)
        Utils.clearConversationId();
		window.localStorage.removeItem('xpressAuth');
		window.localStorage.removeItem('xpress.operator.dashboardTour.firstLogin');
		window.sessionStorage.removeItem('xpressAuth');
        window.sessionStorage.removeItem('currentMenuItem');
        window.sessionStorage.removeItem('clientLocationType');

        const path = this.currentPathname();
        if (path.startsWith('/opstower')) {
            this.redirectTo('/opstower/login');
            return;
        }
        if (path.startsWith('/region')) {
            this.redirectTo('/region/login');
            return;
        }
        if (path.startsWith('/operator')) {
            this.redirectTo('/operator/login');
            return;
        }

        if (this.authData.roleId === XpressUserRoleEnum.TourOperator+'') {
            this.redirectTo('/Xpress/TourOperatorLogin');
            return;
        }
        if (this.xpressUserRoleId === XpressUserRoleEnum.Merchant) {
            this.redirectTo('/Xpress/MerchantLogin');
        } else if (this.xpressUserRoleId === XpressUserRoleEnum.Operator) {
            this.redirectTo('/operator/login');
        } else if (this.xpressUserRoleId === XpressUserRoleEnum.RegionAdmin) {
            this.redirectTo('/region/login');
        } else if (this.xpressUserRoleId === XpressUserRoleEnum.CommandCenterAdmin) {
            this.redirectTo('/opstower/login');
        } else {
            this.redirectTo('/Xpress/Login');
        }
    }

    public betaFeaturesIsExist(searchBetaFeatures: string[]) {
        const betaFeatures = this.authData.BetaFeatures.split(';');

        return searchBetaFeatures.some(p => betaFeatures.includes(p));
    }

    public getServiceTypesCurrentLocationType(): ServiceTypeEnum[] {
        const locationType = this.clientLocationType.getValue();
        if (locationType === XpressClientLocationTypeEnum.Boracay) {
			return [ServiceTypeEnum.Single, ServiceTypeEnum.Charter, ServiceTypeEnum.DayTrip];
        } else if (locationType === XpressClientLocationTypeEnum.Dumaguete) {
            return [ServiceTypeEnum.Charter];
        } else {
            return [ServiceTypeEnum.Ride, ServiceTypeEnum.Taxi, ServiceTypeEnum.Delivery, ServiceTypeEnum.Connex];
        }
    }
}
